import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { products } from "@/mocks/products";

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const product = products.find((p) => p.id === Number(id));
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeTab, setActiveTab] = useState<"desc" | "specs" | "shipping">("desc");

  useEffect(() => {
    if (!product) return;
    document.title = product.name + " – Buy Wholesale | Crown Prime Mall";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Buy " + product.name + " wholesale from " + product.supplier + ". Min. order " + product.minOrder + " " + product.unit + " at $" + product.price + " USD. Verified USA & Canada factory supplier. Fast delivery, secure payment.");
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-product-detail";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "image": product.image,
      "description": product.description || product.name,
      "sku": product.id,
      "brand": {
        "@type": "Brand",
        "name": product.supplier
      },
      "offers": {
        "@type": "Offer",
        "url": siteUrl + "/products/" + product.id,
        "priceCurrency": "USD",
        "price": product.price,
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": product.supplier
        }
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": product.rating,
        "reviewCount": product.reviews
      }
    });
    if (!document.getElementById("schema-product-detail")) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById("schema-product-detail");
      if (el) el.remove();
    };
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdf8f8]">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-5xl text-gray-300"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-700 mb-2">Product Not Found</h2>
          <Link to="/products" className="text-[#C41E3A] text-sm hover:underline cursor-pointer">
            &larr; Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAddToCart = () => {
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  return (
    <div className="min-h-screen bg-[#fdf8f8]">
      {/* Top Bar */}
      <div className="bg-[#1a0a0a] text-white text-xs text-center py-2 px-4">
        FREE SHIPPING on orders over $50 &nbsp;|&nbsp; Secure USD Payments &bull; Verified Suppliers
      </div>

      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 cursor-pointer whitespace-nowrap flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="Crown Prime Mall"
              className="h-9 w-9 rounded-full object-cover"
            />
            <div>
              <div className="font-bold text-sm text-[#C41E3A] leading-tight">Crown Prime Mall</div>
              <div className="text-[10px] text-gray-400">USA &amp; Canada Marketplace</div>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <Link to="/" className="hover:text-[#C41E3A] cursor-pointer whitespace-nowrap">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-[#C41E3A] cursor-pointer whitespace-nowrap">Products</Link>
            <span>/</span>
            <span className="text-gray-800 font-medium truncate max-w-[200px]">{product.name}</span>
          </div>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        {/* Main Product Section */}
        <div className="flex flex-col lg:flex-row gap-10 mb-14">
          {/* Images */}
          <div className="lg:w-2/5">
            <div className="relative rounded-lg overflow-hidden bg-white border border-gray-100 h-96 mb-3">
              <img
                src={product.images[selectedImage] || product.image}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
              {product.badge && (
                <div className="absolute top-4 left-4 bg-[#C41E3A] text-white text-xs font-bold px-3 py-1 rounded">
                  {product.badge}
                </div>
              )}
              <div className="absolute top-4 right-4 bg-[#D4AF37] text-white text-xs font-bold px-3 py-1 rounded">
                -{discount}%
              </div>
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-2">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors cursor-pointer flex-shrink-0 ${
                      selectedImage === i ? "border-[#C41E3A]" : "border-gray-200"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="lg:w-3/5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs bg-[#C41E3A]/10 text-[#C41E3A] px-2 py-0.5 rounded font-medium">{product.category}</span>
              <span className="text-xs text-gray-400">{product.warehouse === "Both" ? "🇺🇸🇨🇦 USA & Canada" : product.warehouse === "USA" ? "🇺🇸 USA Warehouse" : "🇨🇦 Canada Warehouse"}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3 leading-tight">{product.name}</h1>

            {/* Rating */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className={i < Math.floor(product.rating) ? "ri-star-fill text-[#D4AF37] text-sm" : "ri-star-line text-[#D4AF37] text-sm"}></i>
                  </div>
                ))}
                <span className="text-sm font-semibold text-gray-700 ml-1">{product.rating}</span>
              </div>
              <span className="text-sm text-gray-400">({product.reviews.toLocaleString()} reviews)</span>
              <span className="text-sm text-gray-400">{product.sold.toLocaleString()} sold</span>
            </div>

            {/* Price */}
            <div className="flex items-end gap-3 mb-6 pb-6 border-b border-gray-100">
              <span className="text-4xl font-extrabold text-[#C41E3A]">${product.price}</span>
              <span className="text-lg text-gray-400 line-through mb-1">${product.originalPrice}</span>
              <span className="text-sm bg-[#C41E3A] text-white px-2 py-0.5 rounded font-bold mb-1">-{discount}% OFF</span>
            </div>

            {/* Supplier */}
            <div className="flex items-center gap-3 mb-6 p-4 bg-white rounded-lg border border-gray-100">
              <div className="w-10 h-10 flex items-center justify-center bg-[#C41E3A]/10 rounded-full flex-shrink-0">
                <i className="ri-building-2-line text-[#C41E3A]"></i>
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900 text-sm">{product.supplier}</div>
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-map-pin-line"></i>{product.supplierLocation}
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href="https://wa.me/1234567890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 bg-[#25D366] text-white text-xs px-3 py-2 rounded-full font-semibold hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i> WhatsApp
                </a>
                <button className="flex items-center gap-1 bg-[#C41E3A] text-white text-xs px-3 py-2 rounded-full font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-message-3-line"></i> CrownChat
                </button>
              </div>
            </div>

            {/* Quantity & Order */}
            <div className="mb-6">
              <div className="text-sm font-semibold text-gray-700 mb-2">
                Quantity <span className="text-gray-400 font-normal">(Min. {product.minOrder} {product.unit})</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQuantity(Math.max(product.minOrder, quantity - product.minOrder))}
                    className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-16 text-center text-sm font-semibold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + product.minOrder)}
                    className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <div className="text-sm text-gray-500">
                  Total: <span className="font-bold text-[#C41E3A]">${(product.price * quantity).toFixed(2)} USD</span>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={handleAddToCart}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                  addedToCart
                    ? "bg-green-500 text-white"
                    : "bg-[#C41E3A] text-white hover:bg-[#a01830]"
                }`}
              >
                {addedToCart ? (
                  <><i className="ri-check-line"></i> Added to Cart!</>
                ) : (
                  <><i className="ri-shopping-cart-2-line"></i> Add to Cart</>
                )}
              </button>
              <button className="flex-1 flex items-center justify-center gap-2 border-2 border-[#C41E3A] text-[#C41E3A] py-3 rounded-full font-bold text-sm hover:bg-[#C41E3A] hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-flashlight-line"></i> Buy Now
              </button>
            </div>

            {/* Shipping Info */}
            <div className="bg-[#fdf8f8] rounded-lg p-4 text-xs text-gray-600 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-truck-line text-[#C41E3A]"></i></div>
                <span>{product.shipping}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-line text-[#C41E3A]"></i></div>
                <span>Secure USD payment — Visa, Mastercard, PayPal accepted</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-[#C41E3A]"></i></div>
                <span>30-day return policy for verified buyers</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-100 mb-14">
          <div className="flex border-b border-gray-100">
            {(["desc", "specs", "shipping"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? "text-[#C41E3A] border-b-2 border-[#C41E3A]"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {tab === "desc" ? "Description" : tab === "specs" ? "Specifications" : "Shipping & Payment"}
              </button>
            ))}
          </div>
          <div className="p-6">
            {activeTab === "desc" && (
              <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
            )}
            {activeTab === "specs" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.specs.map((spec) => (
                  <div key={spec.label} className="flex justify-between py-2 border-b border-gray-50 text-sm">
                    <span className="text-gray-500 font-medium">{spec.label}</span>
                    <span className="text-gray-900 font-semibold">{spec.value}</span>
                  </div>
                ))}
              </div>
            )}
            {activeTab === "shipping" && (
              <div className="space-y-4 text-sm text-gray-600">
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#C41E3A]/10 rounded-lg flex-shrink-0">
                    <i className="ri-truck-line text-[#C41E3A]"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Shipping</div>
                    <p>{product.shipping}. USA orders: 2-5 business days. Canada orders: 3-6 business days.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#C41E3A]/10 rounded-lg flex-shrink-0">
                    <i className="ri-secure-payment-line text-[#C41E3A]"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Payment</div>
                    <p>All payments processed in USD. We accept Visa, Mastercard, American Express, and PayPal. Secure SSL-encrypted checkout.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#C41E3A]/10 rounded-lg flex-shrink-0">
                    <i className="ri-refresh-line text-[#C41E3A]"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Returns</div>
                    <p>30-day return policy. Items must be unused and in original packaging. Contact us via WhatsApp or CrownChat to initiate a return.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div>
            <h2 className="text-xl font-extrabold text-gray-900 mb-6">Related Products</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-[#C41E3A] transition-all group cursor-pointer block"
                >
                  <div className="h-40 overflow-hidden bg-gray-50">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-3">
                    <div className="text-xs font-semibold text-gray-900 line-clamp-2 mb-1">{p.name}</div>
                    <div className="text-sm font-extrabold text-[#C41E3A]">${p.price}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
